import React, { forwardRef, useState, useEffect } from "react";
import {
  Slide,
  Paper,
  Typography,
  Box,
  IconButton,
  Button,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WifiTetheringIcon from "@mui/icons-material/WifiTethering";
import { Link as RouterLink } from "react-router-dom";
import CircleIcon from "@mui/icons-material/Circle";
import { keyframes } from "@mui/system";
import { formatDate, createUrlSlug } from "./functions";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "./firebase";

// Slide transition component
const SlideTransition = forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

// Global variable to store the function that shows notifications
let showQuizStartedNotificationFunction = null;

// Simple Pulsating animation component for the live icon
const PulsatingIcon = () => {
  // Improved ripple border animation
  const rippleBorder = keyframes`
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.7);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 0 0 9px rgba(255, 82, 82, 0.3);
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(255, 82, 82, 0);
    }
  `;

  // Added pulse effect for the icon itself
  const pulseEffect = keyframes`
    0% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(0.95);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  `;

  return (
    <Box
      sx={{
        marginRight: 1,
        mt: 0.5,
        display: "inline-flex",
        borderRadius: 30,
        p: 0,
        position: "relative",
        "&::before, &::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          zIndex: 0,
        },
        "&::before": {
          animation: `${rippleBorder} 2.5s cubic-bezier(.25,.46,.45,.94) infinite`,
        },
        "&::after": {
          animation: `${rippleBorder} 2s cubic-bezier(.25,.46,.45,.94) infinite 0.4s`,
        },
      }}
    >
      <CircleIcon
        sx={{
          width: 20,
          height: 20,
          color: "#FF5252",
          position: "relative",
          zIndex: 1,
          filter: "drop-shadow(0 0 2px rgba(255, 82, 82, 0.7))",
          animation: `${pulseEffect} 2s ease-in-out infinite`,
        }}
      />
    </Box>
  );
};

// The slide notification component
const QuizStartedNotification = ({
  theme,
  styles,
  debugMode = false,
  currentUserId,
}) => {
  const [open, setOpen] = useState(debugMode);
  const [notification, setNotification] = useState(
    debugMode
      ? {
          quizName: "Debug Quiz",
          originalQuizId: "debug-123",
          duration: 99999999,
          onAction: () => console.log("Debug action clicked"),
          onClose: () => console.log("Debug notification closed"),
          createdAt: Date.now(),
        }
      : null,
  );
  const [timeoutId, setTimeoutId] = useState(null);

  // Listen for quiz started notifications directly
  useEffect(() => {
    if (currentUserId) {
      const quizNotificationsQuery = query(
        collection(db, "notifications"),
        where("recipientId", "==", currentUserId),
        where("type", "==", "quiz_started"),
        where("read", "==", false),
        orderBy("createdAt", "desc"),
      );

      const unsubscribe = onSnapshot(quizNotificationsQuery, (snapshot) => {
        const newNotifications = snapshot
          .docChanges()
          .filter((change) => change.type === "added")
          .map((change) => ({ id: change.doc.id, ...change.doc.data() }));

        if (newNotifications.length > 0) {
          const latestNotification = newNotifications[0];
          showNotification({
            id: latestNotification.id,
            quizName: latestNotification.quizStartData?.quizName || "Quiz",
            originalQuizId: latestNotification.quizStartData?.originalQuizId,
            duration: 99999999,
            createdAt: Date.now(),
          });
        }
      });

      return () => unsubscribe();
    }
  }, [currentUserId]);

  // Quiz started notification style
  const quizStartedStyle = {
    borderLeft: `7px solid ${theme.palette.primary.main}`,
    borderRadius: 4,
    backgroundImage: "none",
    button: {
      "&.MuiButton-root": {
        background: `linear-gradient(135deg, #03A9F4 0%, #0288D1 100%)`,
        "&:hover": {
          background: `linear-gradient(135deg, #0288D1 0%, #01579B 100%)`,
        },
      },
    },
  };

  // Function to show a quiz started notification
  const showNotification = (quizData) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const notificationData = {
      ...quizData,
      createdAt: quizData.createdAt || new Date(),
    };

    setNotification(notificationData);
    setOpen(true);

    const id = setTimeout(() => {
      setOpen(false);
    }, quizData.duration || 9999999);

    setTimeoutId(id);
  };

  // Store the function in the global variable
  useEffect(() => {
    showQuizStartedNotificationFunction = showNotification;
    return () => {
      showQuizStartedNotificationFunction = null;
    };
  }, []);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, "notifications", notificationId);
      await updateDoc(notificationRef, {
        read: true,
      });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Handle close
  // Handle close
  const handleClose = () => {
    setOpen(false);

    // Mark notification as read if we have a valid notification ID
    if (notification && notification.id) {
      markAsRead(notification.id);
    }

    // Call the onClose callback if provided
    if (notification && notification.onClose) {
      notification.onClose();
    }
  };

  if (!notification) return null;

  return (
    <SlideTransition in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={6}
        sx={{
          position: "fixed",
          top: 16,
          left: { xs: 5, md: 15 },
          width: { xs: "360px", sm: 480 },
          zIndex: 9999,
          borderRadius: 2,
          overflow: "hidden",
          ...quizStartedStyle,
        }}
      >
        <Box
          sx={{
            display: "flex",
            p: 2,
            alignItems: "flex-start",
          }}
        >
          <PulsatingIcon />

          <Box sx={{ flexGrow: 1, ml: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Eleverna har börjat svara på "{notification.quizName}"
            </Typography>

            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block" }}
            >
              {notification.createdAt ? formatDate(notification.createdAt) : ""}
            </Typography>

            <Box sx={{ display: "flex", mt: 1 }}>
              <Button
                variant="purp"
                size="small"
                component={RouterLink}
                to={`/app/resultat/${notification.originalQuizId}`}
                sx={{
                  textTransform: "none",
                  ...(styles?.tightButton || {}),
                }}
                onClick={() => {
                  if (notification.id) markAsRead(notification.id);
                  if (notification.onAction) notification.onAction();
                  handleClose();
                }}
                startIcon={<WifiTetheringIcon />}
              >
                Övervaka resultaten
              </Button>
            </Box>
          </Box>

          <IconButton
            size="small"
            aria-label="stäng"
            onClick={handleClose}
            sx={{ ml: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </SlideTransition>
  );
};

// Export a global function to show quiz started notifications
export const showQuizStartedNotification = (quizData) => {
  if (showQuizStartedNotificationFunction) {
    showQuizStartedNotificationFunction(quizData);
  } else {
    console.warn("QuizStartedNotification component is not mounted");
  }
};

export default QuizStartedNotification;
